import { layout } from '@prisjakt/ui-base';
import { TranslateFunc } from '@prisjakt/ui-translate/src/translate-func';
import { ClientMarket } from 'config/types/client-market';
import { AppState } from 'src/reducers';
import { Campaign, CampaignBanner, Product } from 'src/types';
import { CurrentCampaign } from '../types';
import modalActions from 'src/components/modal/actions';
import { Property } from 'src/components/data-list/types';

export const getHeaderHeight = (isMobile: boolean | undefined) =>
  isMobile
    ? layout.headerHeight.mobile.value
    : layout.headerHeight.desktop.value;

export const getScrollLinkOffset = () => {
  const isMobile = window.innerWidth < 640;
  const extraWhitespace = 20;

  return (
    getHeaderHeight(isMobile) +
    layout.stickyNavigationHeight.value +
    extraWhitespace
  );
};

export const getCurrentCampaign = (
  activeCampaign: Campaign,
  product: Product
) => {
  return (
    activeCampaign &&
    product.campaigns &&
    product.campaigns
      .filter(c => c && String(c.id) === String(activeCampaign.id))
      .map<CurrentCampaign>(campaign => {
        return {
          ...campaign,
          name: activeCampaign.name || '',
          path: `${
            activeCampaign.web_uri ? activeCampaign.web_uri.responsive : ''
          }${product.category ? `?category=${product.category.id}` : ''}`,
          media: { ...activeCampaign.media! },
        } as CurrentCampaign;
      })
      .find(() => true)
  );
};

export const getCampaignPromoBanners = (campaignBanners: {
  active: (CampaignBanner & {
    campaign: Pick<Campaign, 'media'>;
  })[];
}) => {
  return {
    before:
      campaignBanners &&
      campaignBanners.active.filter(b => b && b.type === 'BEFORE_PROMO_BANNER'),
    during:
      campaignBanners &&
      campaignBanners.active.filter(b => b && b.type === 'DURING_PROMO_BANNER'),
  };
};

export const blurActiveElement = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

// Transform some basic information about the product into a DataList-friendly shape to pass into ProductProperties
export const basicInfo = (product: Product, t: TranslateFunc): Property[] => {
  if (!product) {
    return [];
  }

  const { brand, category } = product;
  const brandProperties: Property[] = brand
    ? [
        {
          name: t('product', 'property.brand'),
          value: {
            type: 'string',
            text: brand.name,
            link: brand.pathName,
          },
        },
      ]
    : [];
  const categoryProperties: Property[] = category
    ? [
        {
          name: t('product', 'property.category'),
          value: {
            type: 'string',
            text: category.name,
            link: category.pathName,
          },
        },
      ]
    : [];
  const productDefaults: Property[] = [
    {
      name: t('product', 'property.product-name'),
      value: { type: 'string', text: product.name },
    },
  ];
  return [...productDefaults, ...brandProperties, ...categoryProperties];
};

export const mapStateToProps = (state: AppState) => ({
  campaignBanners: state.globals.campaignBanners,
  review: state.areas.review,
  currentModal: state.modal.current,
  me: state.areas.account.me,
});

export const dispatchProps = {
  hideModal: modalActions.hideModal,
};

const getDealsEndpoint = (market: ClientMarket) => {
  return `${market.campaignsBaseUrl}/campaign-${market.code}-*-deals/_search`;
};

export const fetchDeals = (market: ClientMarket, productId: number) => {
  const dealsEndpoint = getDealsEndpoint(market);
  return fetch(dealsEndpoint, {
    method: 'POST',
    body: JSON.stringify({
      _source: ['priceInfo.priceId', 'campaign.id'],
      query: {
        bool: {
          filter: {
            terms: { 'product.id': [productId] },
          },
        },
      },
    }),
  })
    .catch(err => {
      const fetchError = new Error(
        `FetchError: ${err.message} ${dealsEndpoint}`
      );
      throw fetchError;
    })
    .then(res => res.json());
};
